import CheckBox from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank'
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { loadOptionsAdvice } from '../../../PrescriptionMakingPageComponents'

function normalize(value) {
  return value == null ? '' : String(value).trim().toLowerCase()
}

/**
 * A compact, scrollable checkbox list of the doctor's frequently-used advices, shown below the
 * advice search box for one-tap selection. Sourced from the same loadOptionsAdvice pipeline the
 * search uses (recommended + intel + cached), so it reflects "frequently used" without any new
 * endpoint. Toggling a box adds/removes the advice from the selected list; boxes stay in sync
 * with items added via the search. Height is capped with an internal scrollbar so it never grows
 * tall. Frontend-only — the stored advice shape is unchanged.
 */
export default function AdviceFrequentlyUsedCheckboxes({
  advices,
  setAdvices,
  clinicId,
  clinicBrandId,
  recommendedAdvices = [],
  prescriptionViewSettings,
}) {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(true)

  const recommendedKey = (recommendedAdvices || []).join(',')

  useEffect(() => {
    let cancelled = false
    async function fetchFrequentlyUsed() {
      setLoading(true)
      try {
        const res = await loadOptionsAdvice('', [], {
          page: 1,
          clinicId,
          clinicBrandId,
          prescriptionViewSettings,
          recommendedAdvices,
        })
        if (cancelled) {
          return
        }
        const seen = new Set()
        const deduped = []
        ;(res?.options || []).forEach((option) => {
          if (!option?.label) {
            return
          }
          const key = normalize(option?.value ?? option?.label)
          if (key && !seen.has(key)) {
            seen.add(key)
            deduped.push(option)
          }
        })
        setOptions(deduped)
      } catch (e) {
        if (!cancelled) {
          setOptions([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchFrequentlyUsed()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, clinicBrandId, recommendedKey])

  const selectedKeys = useMemo(() => {
    const set = new Set()
    ;(advices || []).forEach((advice) => {
      const key = normalize(advice?.value ?? advice?.label)
      if (key) {
        set.add(key)
      }
    })
    return set
  }, [advices])

  function toggleAdvice(option, checked) {
    const key = normalize(option?.value ?? option?.label)
    if (checked) {
      setAdvices((prev) => {
        const list = prev || []
        if (list.some((advice) => normalize(advice?.value ?? advice?.label) === key)) {
          return list
        }
        return [...list, option]
      })
    } else {
      setAdvices((prev) =>
        (prev || []).filter((advice) => normalize(advice?.value ?? advice?.label) !== key),
      )
    }
  }

  // Nothing to offer — stay out of the way.
  if (!loading && options.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 0.75,
        }}
      >
        Frequently used
      </Typography>
      <Box
        sx={{
          maxHeight: 168,
          overflowY: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.25,
          p: 1,
          borderRadius: '10px',
          border: '1px solid rgba(67, 56, 202, 0.12)',
          background: 'rgba(67, 56, 202, 0.02)',
          // Slim, unobtrusive scrollbar.
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(67, 56, 202, 0.25)',
            borderRadius: '3px',
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
            <CircularProgress size={16} sx={{ color: '#4338ca' }} />
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>Loading suggestions…</Typography>
          </Box>
        ) : (
          options.map((option, index) => {
            const key = normalize(option?.value ?? option?.label)
            const checked = selectedKeys.has(key)
            return (
              <FormControlLabel
                key={`${key}-${index}`}
                sx={{
                  m: 0,
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 4px)' },
                  minWidth: 0,
                  borderRadius: '8px',
                  px: 0.5,
                  transition: 'background 0.15s ease',
                  background: checked ? 'rgba(67, 56, 202, 0.06)' : 'transparent',
                  '&:hover': { background: 'rgba(67, 56, 202, 0.06)' },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={(e) => toggleAdvice(option, e.target.checked)}
                    icon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
                    checkedIcon={<CheckBox sx={{ fontSize: 18 }} />}
                    sx={{ py: 0.5, color: 'rgba(67, 56, 202, 0.5)', '&.Mui-checked': { color: '#4338ca' } }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: '#1e293b',
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                    }}
                  >
                    {option?.label}
                  </Typography>
                }
              />
            )
          })
        )}
      </Box>
    </Box>
  )
}
